import cx from 'classnames'
import { match } from '@/utils/pattern-match-util'
import { m } from 'motion/react'
import styles from './CircularProgressbar.module.css'

export interface SpinnerProps {
  size?: number
  thickness?: number
  variant?: CircularProgressbarVariant

  /**
   * Value between 0 and 100 indicating the progress.
   */
  progress?: number
}

export type CircularProgressbarVariant = 'default' | 'invert' | 'inherit'

/**
 * A circular progress bar.
 */
export function CircularProgressbar({
  variant = 'default',
  size = 24,
  thickness = 3,
  progress = 0,
}: SpinnerProps) {
  // Ensure progress is clamped between 0 and 100
  const clampedProgress = Math.min(100, Math.max(0, progress))

  // Calculate stroke-dasharray and stroke-dashoffset
  const radius = (size - thickness) / 2 // Adjust for stroke width
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset =
    circumference - (clampedProgress / 100) * circumference

  return (
    <div
      className={cx(
        styles.root,
        match(variant, {
          default: styles.default,
          invert: styles.invert,
          inherit: styles.inherit,
        }),
      )}
      style={{
        width: size,
        height: size,
      }}
    >
      <svg
        className={styles.svg}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeOpacity="0.24"
          strokeWidth={thickness}
          className={styles.backgroundCircle}
        />

        {/* Progress circle */}
        <m.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          // Rotate to start at top
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference.toFixed(2)}
          strokeDashoffset={strokeDashoffset.toFixed(2)}
          animate={{
            strokeDashoffset: strokeDashoffset,
          }}
          className={styles.progressCircle}
        />
      </svg>
    </div>
  )
}
